import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { AuthPasswordField } from '@nesso/ui/primitives/auth-password-field';
import { Button } from '@nesso/ui/primitives/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@nesso/ui/primitives/dialog';

import { confirmIdentityInputSchema } from '@/entities/user';

const formSchema = z.object({
    currentPassword: z
        .string()
        .min(1, 'Enter your current password')
        .pipe(confirmIdentityInputSchema.shape.currentPassword),
});

type FormValues = z.input<typeof formSchema>;

type Props = {
    pending: boolean;
    error?: string;
    onContinue: (currentPassword: string) => void;
    onCancel: () => void;
};

export function ProfileEmailChangeConfirmIdentityStep({
    pending,
    error,
    onContinue,
    onCancel,
}: Props) {
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: { currentPassword: '' },
        mode: 'onChange',
    });

    useEffect(() => {
        if (error) form.setError('currentPassword', { type: 'server', message: error });
    }, [error, form]);

    const submit = form.handleSubmit((values) => onContinue(values.currentPassword));

    const currentPassword = form.watch('currentPassword');

    return (
        <>
            <DialogHeader className="text-left">
                <DialogTitle>Confirm your identity to update your email</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="flex flex-col gap-6">
                <Controller
                    control={form.control}
                    name="currentPassword"
                    render={({ field, fieldState }) => {
                        const showError =
                            fieldState.error?.type === 'server' ||
                            fieldState.isTouched ||
                            form.formState.submitCount > 0;
                        return (
                            <AuthPasswordField
                                ref={field.ref}
                                id="email-change-current-password"
                                label="Current password"
                                autoComplete="current-password"
                                value={field.value}
                                error={showError ? fieldState.error?.message : undefined}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={pending}
                            />
                        );
                    }}
                />
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={pending || currentPassword.length === 0}>
                        {pending ? 'Confirming…' : 'Continue'}
                    </Button>
                </DialogFooter>
            </form>
        </>
    );
}
